import { Trans, t } from '@lingui/macro';
import { Menu, Search, Star, Share2, X, MoreVertical, Image, FileText, Mic, Video, Filter, Users, Languages } from 'lucide-react';
import { ArrowLeft, PanelRight, PanelRightOpen, SlidersHorizontal, Trash2, Columns2, Settings, Building2 } from 'lucide-react';
import { useEffect, useState, useRef, useCallback } from 'react';
import { useHistory } from '../../hooks/useRouterCompat';
import { useAppUIContext } from '../../context/AppUIContext';
import { Tran } from '../../locales/Tran';
import { Room } from '../../models/Room';
import { RoomCall } from '../../models/RoomCall';
import { Workspace } from '../../models/Workspace';
import { FilterType } from '../../models/local/Types';
import { Fire } from '../../services/FireClient';
import { My } from '../../services/My';
import { MyNotifs } from '../../services/MyNotifs';
import { twil } from '../../services/TwilioHelper';
import CallHeaderPreview from '../components/CallHeaderPreview';
import FilterPopover from '../components/FilterPopover';
import MyRoomOptionModal, { getExitNavigationPath } from '../components/MyRoomOptionModal';
import { Button } from '../../components/ui/button';
import { ButtonGroup } from '../../components/ui/button-group';
import { Badge } from '../../components/ui/badge';
import { Input } from '../../components/ui/input';
import { cn } from '../../lib/utils';
import { getAutoTranslate, setAutoTranslate, AutoTranslateEmitter } from '../../services/translation/autoTranslate';
import { useResponsive, showRightMenu } from '../../hooks/useResponsive';
import { DropdownMenu, DropdownMenuContent, DropdownMenuItem, DropdownMenuTrigger, DropdownMenuSeparator, DropdownMenuSub, DropdownMenuSubTrigger, DropdownMenuSubContent } from '../../components/ui/dropdown-menu';
import { useToast } from '../../hooks/use-toast';
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from '../../components/ui/alert-dialog';

interface MobileHeaderActionsProps {
  roomId: string;
  onSearchModeChange?: (mode: boolean) => void;
  gotoFavs: () => void;
  gotoShares: () => void;
  isFavoritesMode: boolean;
  isPersonalRoom?: boolean;
  filter: FilterType;
  setFilter: (type: FilterType) => void;
}

/**
 * Figma: Mobile/워크스페이스 (119:16219)
 * 모바일 헤더 우측: ⋮ 드롭다운 + 패널 토글 (2개만)
 * 검색/즐겨찾기/공유는 ⋮ 드롭다운 안에 통합
 */
const MobileHeaderActions: React.FC<MobileHeaderActionsProps> = ({
  roomId,
  onSearchModeChange,
  gotoFavs,
  gotoShares,
  isFavoritesMode,
  isPersonalRoom,
  filter,
  setFilter,
}) => {
  const [autoOn, setAutoOn] = useState(() => getAutoTranslate(roomId));
  useEffect(() => {
    setAutoOn(getAutoTranslate(roomId));
    const h = (rid: string) => { if (rid === roomId) setAutoOn(getAutoTranslate(rid)); };
    AutoTranslateEmitter.on('change', h);
    return () => { AutoTranslateEmitter.removeListener('change', h); };
  }, [roomId]);
  const changeType = (t: FilterType) => {
    setFilter(t);
  };

  return (
    <DropdownMenu>
      <DropdownMenuTrigger asChild>
        <Button variant="icon-panel" size="icon">
          <MoreVertical className="h-5 w-5" />
        </Button>
      </DropdownMenuTrigger>
      <DropdownMenuContent
        align="end"
        className="min-w-[160px] rounded-xl border-border shadow-[0_0_4px_rgba(0,0,0,0.12)]"
      >
        <DropdownMenuItem onClick={() => onSearchModeChange?.(true)}>
          <Search className="h-4 w-4 mr-2" />
          <Trans>Search</Trans>
        </DropdownMenuItem>
        <DropdownMenuItem onClick={gotoFavs}>
          <Star className={cn("h-4 w-4 mr-2", isFavoritesMode && "fill-[rgb(var(--figma-gray-500))]")} />
          <Trans>Favorites</Trans>
        </DropdownMenuItem>
        <DropdownMenuItem onClick={gotoShares}>
          <Share2 className="h-4 w-4 mr-2" />
          <Trans>Shared Links</Trans>
        </DropdownMenuItem>
        <DropdownMenuItem onClick={() => setAutoTranslate(roomId, !autoOn)}>
          <Languages className={cn("h-4 w-4 mr-2", autoOn && "text-primary")} />
          <Trans>Auto-translate</Trans>
          {autoOn && <span className="ml-auto text-primary">✓</span>}
        </DropdownMenuItem>
        {!isPersonalRoom && (
          <>
            <DropdownMenuSeparator />
            <DropdownMenuSub>
              <DropdownMenuSubTrigger>
                <Filter className="h-4 w-4 mr-2" />
                <Trans>Filter messages</Trans>
              </DropdownMenuSubTrigger>
              <DropdownMenuSubContent className="min-w-[160px] rounded-xl border-border">
                <DropdownMenuItem onClick={() => changeType('photos')} className={cn(filter === 'photos' && "text-primary bg-primary/10")}>
                  <Image className="h-4 w-4 mr-2" />
                  <Trans>Photos</Trans>
                </DropdownMenuItem>
                <DropdownMenuItem onClick={() => changeType('docs')} className={cn(filter === 'docs' && "text-primary bg-primary/10")}>
                  <FileText className="h-4 w-4 mr-2" />
                  <Trans>Docs</Trans>
                </DropdownMenuItem>
                <DropdownMenuItem onClick={() => changeType('voice')} className={cn(filter === 'voice' && "text-primary bg-primary/10")}>
                  <Mic className="h-4 w-4 mr-2" />
                  <Trans>Voice</Trans>
                </DropdownMenuItem>
                <DropdownMenuItem onClick={() => changeType('video')} className={cn(filter === 'video' && "text-primary bg-primary/10")}>
                  <Video className="h-4 w-4 mr-2" />
                  <Trans>Screen Recording</Trans>
                </DropdownMenuItem>
              </DropdownMenuSubContent>
            </DropdownMenuSub>
          </>
        )}
      </DropdownMenuContent>
    </DropdownMenu>
  );
};

interface RoomHeaderProps {
  room: Room;
  type: FilterType;
  inProgress: boolean;
  isSearchMode?: boolean;
  searchQuery?: string;
  isFavoritesMode?: boolean;
  onSearchModeChange?: (mode: boolean) => void;
  onFavoritesModeChange?: (mode: boolean) => void;
  onSearchQueryChange?: (query: string) => void;
  onSearchSubmit?: () => void;
  onRightMenuToggle?: () => void;
  contentWidth?: 'min-width' | 'full';
  onContentWidthChange?: (mode: 'min-width' | 'full') => void;
  tabsSlot?: React.ReactNode;
  /** 모바일 탭 행 우측에 채팅 액션 팝오버(검색/즐겨찾기/공유/필터) 표시 여부 — 채팅 탭 활성일 때만 */
  showChatActions?: boolean;
}

const RoomHeader: React.FC<RoomHeaderProps> = ({
  room,
  inProgress,
  isSearchMode = false,
  searchQuery = "",
  isFavoritesMode = false,
  onSearchModeChange,
  onFavoritesModeChange,
  onSearchQueryChange,
  onSearchSubmit,
  onRightMenuToggle,
  contentWidth = 'min-width',
  onContentWidthChange,
  tabsSlot,
  showChatActions = false
}) => {
  const { filter, setFilter, workspaces, setEnterType, setShowFilesModal, setIsMobileMenuOpen, activeCalls, activeWorkspaceId } = useAppUIContext();
  const history = useHistory();
  const [workspace, setWorkspace] = useState<Workspace>();
  const call = activeCalls.get(room.id);
  const [counter, setCounter] = useState(0);
  const { isSmallMobile, isMobileOrTablet, isSidebarCollapsed } = useResponsive();
  const [isRoomModalOpen, setIsRoomModalOpen] = useState(false);
  const { toast } = useToast();
  const [isMigrating, setIsMigrating] = useState(false);
  const [showDeleteDialog, setShowDeleteDialog] = useState(false);

  const isInCall = twil.isConnected() && twil.getRoomId() == room.id;
  const isPersonalRoom = room.visibility === 'personal';
  // 개인 채널·1:1 대화(direct) 공통: 채팅 전용 레이아웃(탭바 숨김 + 액션바 헤더 inline)
  const isChatOnly = isPersonalRoom || room.visibility === 'direct';
  const isLegacyPersonalRoom = isPersonalRoom && !room.workspaceId;
  const isLegacyRoom = !room.workspaceId && room.isOneToOne;
  const typeTrans = Tran.filterType(filter);
  const [isSearchModalOpen, setIsSearchModalOpen] = useState(false);

  const timerRef = useRef<NodeJS.Timeout | null>(null);
  const isLongPressRef = useRef(false);

  const startPress = useCallback(() => {
    isLongPressRef.current = false;
    timerRef.current = setTimeout(() => {
      isLongPressRef.current = true;
      window.dispatchEvent(new Event('open-workspace-sitemap'));
      if (navigator.vibrate) navigator.vibrate(50);
    }, 500);
  }, []);

  const cancelPress = useCallback(() => {
    if (timerRef.current) {
      clearTimeout(timerRef.current);
    }
  }, []);

  useEffect(() => {
    setFilter('none');
    let unsubNotifs = MyNotifs.subscribeToNotificationUpdates((_) => {
      setCounter(MyNotifs.NrOfNew);
    });

    // 워크스페이스 캐시 우선 활용
    const ws = workspaces.find(w => w.id === room.workspaceId);
    if (ws) {
      setWorkspace(ws);
    } else {
      My.getOrFetchWorkspace(room.workspaceId, workspaces).then(setWorkspace);
    }

    return () => { unsubNotifs(); }
  }, [room.id, room.workspaceId, workspaces]);

  function gotoShares() {
    setEnterType('link');
    setShowFilesModal(true);
  }

  function gotoFavs() {
    // 토글 기능: 현재 즐겨찾기 모드면 해제, 아니면 활성화
    onFavoritesModeChange?.(!isFavoritesMode);
  }

  const handleMigrateWorkspace = async (wsId: string) => {
    if (isMigrating) return;
    setIsMigrating(true);
    try {
      await Fire.updateField(Room.COL, room.id, { workspaceId: wsId });

      // 알림 및 토스트
      toast({
        title: t`Channel Migrated`,
        description: t`This channel has been successfully moved to the selected workspace.`,
      });
    } catch (error) {
      console.error("Migration failed:", error);
      toast({
        title: t`Migration Failed`,
        description: t`An error occurred while moving the channel.`,
        variant: "destructive"
      });
    } finally {
      setIsMigrating(false);
    }
  };

  return (
    <>
      <header className="sticky top-0 z-40 bg-background min-w-0 relative select-none border-b border-border">
        <div className="flex items-center px-4 py-2.5 gap-2 md:gap-4 w-full">
          {/* Left - Menu Button */}
          <div className="shrink-0 lg:hidden">
            <Button
              variant="icon-panel"
              size="icon"
              data-cy='menu'
              onClick={() => {
                if (!isLongPressRef.current) {
                  setIsMobileMenuOpen(true);
                }
              }}
              onTouchStart={startPress}
              onTouchEnd={cancelPress}
              onMouseDown={startPress}
              onMouseUp={cancelPress}
              onMouseLeave={cancelPress}
              style={{ WebkitTouchCallout: 'none', WebkitUserSelect: 'none' }}
              className="relative"
            >
              <Menu className="h-5 w-5" />
              {!!counter && (
                <Badge variant="destructive" className="absolute -top-1 -right-1 h-5 w-5 flex items-center justify-center p-0 rounded-full text-meta-sm">
                  {counter}
                </Badge>
              )}
            </Button>
          </div>

          {/* Center - Title or Search Input */}
          <div className="flex-1 min-w-0">
            {isSearchMode ? (
              // 검색 입력 모드
              <div className="flex items-center gap-2">
                <Button
                  variant="icon-panel"
                  onClick={() => {
                    onSearchModeChange?.(false);
                    onSearchQueryChange?.("");
                  }}
                >
                  <ArrowLeft />
                </Button>
                <Input
                  type="text"
                  variant="basic"
                  placeholder={t`Search messages and file names...`}
                  value={searchQuery}
                  onChange={(e) => onSearchQueryChange?.(e.target.value)}
                  onKeyDown={(e) => {
                    if (e.key === 'Enter' && searchQuery.trim()) {
                      e.preventDefault();
                      onSearchSubmit?.();
                    }
                  }}
                  className="flex-1 my-[4.5px]"
                  autoFocus
                />
                {searchQuery && (
                  <Button
                    variant="icon-panel"
                    onClick={() => onSearchQueryChange?.("")}
                  >
                    <X />
                  </Button>
                )}
              </div>
            ) : (
              // 일반 모드 - 제목(좌) + 가운데 탭바
              <div className="flex items-center gap-2 md:gap-4 min-w-0">
                <div className="min-w-0 shrink">
                {isPersonalRoom ? (
                  <div>
                    <div className="flex items-center gap-2">
                      <h1 className="text-lg font-semibold truncate text-[color:rgb(var(--text-title))]">
                        # <Trans>My Channel</Trans>
                      </h1>
                      {isLegacyPersonalRoom && (
                        <Button
                          variant="icon-panel"
                          onClick={() => setShowDeleteDialog(true)}
                          title={t`Delete this channel`}
                        >
                          <Trash2 />
                        </Button>
                      )}
                    </div>
                  </div>
                ) : (
                  <>
                    <div className="flex items-center gap-2 max-w-full">
                      <span className="truncate font-medium text-md text-[color:rgb(var(--text-title))]">
                        {Room.getRoomNameWithSharp(room, My.user.id)}
                      </span>
                      <Button
                        variant="icon-panel"
                        onClick={() => setTimeout(() => setIsRoomModalOpen(true), 200)}
                        data-cy="setting"
                      >
                        <SlidersHorizontal />
                      </Button>
                      {typeTrans && (
                        <Badge
                          variant="secondary"
                          className="cursor-pointer flex items-center gap-1 flex-shrink-0"
                          onClick={() => setFilter('none')}
                        >
                          <X className="h-3 w-3" />
                          <span className="text-xs whitespace-nowrap">{typeTrans}</span>
                        </Badge>
                      )}
                    </div>
                  </>
                )}
                </div>
                {tabsSlot && !isSidebarCollapsed && (
                  isChatOnly ? (
                    // 개인 채널: 액션바를 헤더 우측 끝으로 (flex-1 스페이서를 앞에 둬 우측 정렬)
                    <>
                      <div className="flex-1" />
                      <div className="shrink-0">{tabsSlot}</div>
                    </>
                  ) : (
                    <>
                      {/* 데스크탑: 타이틀 우측에 거리를 두고 왼쪽 고정 — 우측 flex-1 스페이서가 폭 변화를 흡수해 탭 위치 고정 */}
                      <div className="shrink-0 md:ml-6">{tabsSlot}</div>
                      <div className="flex-1" />
                    </>
                  )
                )}
              </div>
            )}
          </div>

          {/* Right - Panel Toggle (통화·멤버 스택은 폐기 — 채널 우측 멤버 패널에서 진입) */}
          <div className="flex items-center gap-2 shrink-0">
            {!isSearchMode && (
              <>
                {/* 개인 채널 모바일(<1024, 사이드바 접힘): 액션바(검색/저장/공유 아이콘)를 우측 패널 버튼 왼쪽에 inline.
                    1024~1199 는 헤더 우측(tabsSlot 364)에 그대로 둠 — isSidebarCollapsed 기준으로 364 와 겹치지 않게. */}
                {isChatOnly && isSidebarCollapsed && tabsSlot}
                {/* Right Menu Toggle (< 1200px) */}
                {isMobileOrTablet && (
                  <Button
                    variant="icon-panel"
                    onClick={() => onRightMenuToggle?.()}
                    data-cy="menu"
                  >
                    <PanelRightOpen />
                  </Button>
                )}
              </>
            )}
          </div>

          {/* Progress Bar */}
        </div>

        {/* 모바일(컴팩트, <1024): 탭을 헤더 한 칸 아래 별도 행으로 — 좁은 헤더에서 타이틀이 가려지던 문제 해결.
            채팅 탭 활성 시 행 우측 ⋮ 팝오버로 검색/즐겨찾기/공유/필터 제공 (ChatActionBar 행 대체) */}
        {tabsSlot && isSidebarCollapsed && !isSearchMode && !isChatOnly && (
          // min-h-11: ⋮ 액션 버튼(36px)+pb-2 기준 고정 높이 — 채팅 외 탭에서 버튼이 사라져도 행 높이가 줄어 탭이 점프하지 않도록
          // (개인 채널은 별도 행 없이 헤더 1행 우측 패널 버튼 왼쪽에 액션바 inline — 위 Right 영역 참고)
          <div className="flex min-h-11 items-center px-4 pb-2">
            {tabsSlot}
            {showChatActions && (
              <>
                <div className="flex-1" />
                <MobileHeaderActions
                  roomId={room.id}
                  onSearchModeChange={onSearchModeChange}
                  gotoFavs={gotoFavs}
                  gotoShares={gotoShares}
                  isFavoritesMode={isFavoritesMode}
                  isPersonalRoom={isPersonalRoom}
                  filter={filter}
                  setFilter={setFilter}
                />
              </>
            )}
          </div>
        )}
        {inProgress && (
          <div className="absolute bottom-0 left-0 right-0 h-1 bg-muted overflow-hidden">
            <div className="h-full w-full bg-primary animate-pulse" />
          </div>
        )}

        {/* Call Header Preview */}
        {call && !isInCall && <CallHeaderPreview room={room} call={call} />}


      </header>

      {/* Room Options Modal */}
      <MyRoomOptionModal
        room={room}
        workspace={workspace}
        isOpen={isRoomModalOpen}
        onOpenChange={setIsRoomModalOpen}
      />

      {/* Delete Legacy My Room Confirmation Dialog */}
      <AlertDialog open={showDeleteDialog} onOpenChange={setShowDeleteDialog}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>
              <Trans>Delete this channel</Trans>
            </AlertDialogTitle>
            <AlertDialogDescription>
              <Trans>This action cannot be undone.</Trans>
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel><Trans>Cancel</Trans></AlertDialogCancel>
            <AlertDialogAction
              onClick={async () => {
                try {
                  await Fire.deleteRoom(room.id);
                  history.replace(getExitNavigationPath(room.id, room.workspaceId ?? activeWorkspaceId));
                  toast({ title: t`Channel deleted`, duration: 2000 });
                } catch (error) {
                  console.error('Failed to delete room:', error);
                }
              }}
              className="bg-destructive hover:bg-destructive/90"
            >
              <Trans>Yes, delete</Trans>
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </>
  )
};

export default RoomHeader;
